import React from 'react';
import {
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { Nullable, floatVal } from '@jamesgmarks/utilities';
import { IBaseSubscriptions } from '@llws/lift-entity-interfaces';
import { roundFloatToCents } from '@hydra/internal';
import {
  assertValidBillingType,
  BILLING_FEATURE_BASE_PRICE,
  BILLING_FEATURE_LIFT_BUILDING_PRICES,
  BILLING_FEATURE_PROPERTY_LIST,
  BILLING_FEATURE_VARIABLE_BASE_PRICE,
  billingTypeHasFeature,
  TValidBillingFeature,
  isCustomConfigBillingType,
  BILLING_TYPE_TIERED_UNIT_COUNT,
} from '@llws/hydra-shared';
import { SubscriptionBuildingEditor } from '../subscriptionBuildingEditor';
import {
  VariableBasePriceEditor,
  IVBPData,
} from '../../EditorComponents/VariableBasePriceEditor/VariableBasePriceEditor';
import { CustomConfigEditor } from '../CustomConfigEditor/CustomConfigEditor';
import { CommissionEditor, ICommissionSchemeData } from '../../EditorComponents/CommissionEditor/CommissionEditor';
import { NewCommissionEditor } from '../../EditorComponents/CommissionEditor/NewCommissionEditor';
import { ISubscriptionData } from '../types';
import { TCustomConfiguration } from '../CustomConfigEditor/customConfigMeta';
import { ISubscriptions } from '@llws/lift-entity-interfaces';
import { billingTypeCustomConfigComponentMapping } from 'src/components/pages/Subscriptions/form/utils';

interface ISubscriptionAdvancedFeaturesProps {
  subscriptionData: ISubscriptionData;
  setSubscriptionData: React.Dispatch<React.SetStateAction<ISubscriptionData>>;
  billingTypeId: number;
  billingFeatureMetadata: (billingTypeId: number, feature: TValidBillingFeature, field: string) => unknown;
  onBuildingChanged: (building: { buildingName: string; id: number }, price: string) => void;
  onBuildingRemoved: (id: number) => void;
  vBPData: IVBPData;
  setVBPData: React.Dispatch<React.SetStateAction<IVBPData>>;
  defaultVBPData: IVBPData
  selectedBaseSubscription: Nullable<IBaseSubscriptions>;
  storeSubscription: ISubscriptions | null;
  // Commission props
  isEditingSubscriptionCommission: boolean;
  setIsEditingSubscriptionCommission: React.Dispatch<React.SetStateAction<boolean>>;
  subscriptionId: Nullable<number>;
  commissionSchemeData: ICommissionSchemeData;
  selectedClientSalesRepId: Nullable<number>;
  onUpdateCommissionScheme: (updatedCommissionSchemeData: ICommissionSchemeData) => void;
  fetchCommissionSchemes: (subscriptionId: number) => Promise<void>;
  // New props for commission editor switch
  useTableCommissionEditor?: boolean;
  setUseTableCommissionEditor?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SubscriptionAdvancedFeatures: React.FC<ISubscriptionAdvancedFeaturesProps> = ({
  subscriptionData,
  setSubscriptionData,
  billingTypeId,
  billingFeatureMetadata,
  onBuildingChanged,
  onBuildingRemoved,
  vBPData,
  setVBPData,
  defaultVBPData,
  selectedBaseSubscription,
  storeSubscription,
  // Commission props
  isEditingSubscriptionCommission,
  setIsEditingSubscriptionCommission,
  subscriptionId,
  commissionSchemeData,
  selectedClientSalesRepId,
  onUpdateCommissionScheme,
  fetchCommissionSchemes,
  // New props for commission editor switch
  useTableCommissionEditor = true,
  setUseTableCommissionEditor,
}) => {
  return (
    <Grid container spacing={3}>
      {
        billingTypeHasFeature(assertValidBillingType(billingTypeId), BILLING_FEATURE_LIFT_BUILDING_PRICES)
        && <Grid item container xs={12} spacing={3}>
          <Grid item xs={4}>
            <TextField
              label="Minimum Value"
              type="number"
              size="small"
              fullWidth
              sx={{ background: "white" }}
              value={subscriptionData.liftSubscriptions.minimumValue}
              onChange={(e) => {
                const value = floatVal(e.target.value);
                if (value < 0) return;
                setSubscriptionData((old) => ({
                  ...old,
                  liftSubscriptions: {
                    ...old.liftSubscriptions,
                    minimumValue: roundFloatToCents(value),
                  },
                }));
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Cost per Single Family Home"
              type="number"
              size="small"
              fullWidth
              sx={{ background: "white" }}
              value={subscriptionData.liftSubscriptions.costPerSingleFamilyHome}
              onChange={(e) => {
                const value = floatVal(e.target.value);
                if (value < 0) return;
                setSubscriptionData((old) => ({
                  ...old,
                  liftSubscriptions: {
                    ...old.liftSubscriptions,
                    costPerSingleFamilyHome: roundFloatToCents(value),
                  },
                }));
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Cost per Multi Family Home"
              type="number"
              size="small"
              fullWidth
              sx={{ background: "white" }}
              value={subscriptionData.liftSubscriptions.costPerMultiFamilyHome}
              onChange={(e) => {
                const value = floatVal(e.target.value);
                if (value < 0) return;
                setSubscriptionData((old) => ({
                  ...old,
                  liftSubscriptions: {
                    ...old.liftSubscriptions,
                    costPerMultiFamilyHome: roundFloatToCents(value),
                  },
                }));
              }}
            />
          </Grid>
        </Grid>
      }

      {
        billingTypeHasFeature(assertValidBillingType(billingTypeId), BILLING_FEATURE_PROPERTY_LIST)
        && <Grid item xs={12}>
          <SubscriptionBuildingEditor
            buildingsData={subscriptionData.buildingsData}
            clientAccountId={subscriptionData.clientFreshbooksId}
            onBuildingChanged={onBuildingChanged}
            onBuildingRemoved={onBuildingRemoved}
            onPriceChanged={(id: number, newPrice: string) => {
              setSubscriptionData((old) => ({
                ...old,
                buildingsData: old.buildingsData.map((b) => {
                  if (b.id === id) {
                    return { ...b, price: newPrice };
                  }
                  return b;
                }),
              }));
            }}
            basePrice={
              billingTypeHasFeature(assertValidBillingType(billingTypeId), BILLING_FEATURE_BASE_PRICE)
                ? subscriptionData.basePrice
                : null
            }
            hasEvenBuildingSplit={
              billingTypeHasFeature(assertValidBillingType(billingTypeId), BILLING_FEATURE_PROPERTY_LIST)
              && Boolean(billingFeatureMetadata(
                billingTypeId, BILLING_FEATURE_PROPERTY_LIST, 'allowEvenBuildingSplit',
              ))
            }
            defaultEvenBuildingSplit={subscriptionData.evenBuildingSplit === 1}
            onEvenBuildingSplitChanged={(evenBuildingSplit) => {
              setSubscriptionData((old) => ({ ...old, evenBuildingSplit: evenBuildingSplit ? 1 : 0 }));
            }}
            disabled={
              !billingTypeHasFeature(assertValidBillingType(billingTypeId), BILLING_FEATURE_PROPERTY_LIST)
            }
          />
        </Grid>
      }

      {
        billingTypeHasFeature(assertValidBillingType(billingTypeId), BILLING_FEATURE_VARIABLE_BASE_PRICE)
        && <Grid item xs={12}>
          <VariableBasePriceEditor
            rangedVBP={vBPData.rangedVBP}
            rangedVBPO={vBPData.rangedVBPO}
            defaultVBPData={defaultVBPData}
            onSave={(updatedVBPO) => setVBPData({ rangedVBP: vBPData.rangedVBP, rangedVBPO: updatedVBPO })}
            disabled={
              !billingTypeHasFeature(assertValidBillingType(
                billingTypeId,
              ), BILLING_FEATURE_VARIABLE_BASE_PRICE)
            }
          />
        </Grid>
      }

      {
        billingTypeId === BILLING_TYPE_TIERED_UNIT_COUNT
        && <Grid item xs={12}>
          <CustomConfigEditor
            customConfiguration={subscriptionData?.customConfiguration ?? null}
            baseCustomConfiguration={
              selectedBaseSubscription?.customConfiguration as TCustomConfiguration ?? null
            }
            storeSubscription={storeSubscription}
            componentName={billingTypeCustomConfigComponentMapping[billingTypeId]}
            onSave={(config) => {
              setSubscriptionData((old) => ({
                ...old,
                customConfiguration: config,
              }));
            }}
            disabled={!isCustomConfigBillingType(billingTypeId)}
            hasErrors={
              !selectedBaseSubscription
              || !selectedBaseSubscription.customConfiguration
              || Object.keys(selectedBaseSubscription.customConfiguration).length === 0
            }
          />
        </Grid>
      }

      {/* Commission Editor */}
      {/* TODO: Fix the state management in the Commission Editor and then uncomment*/}
      {/* <Grid item xs={12}>
        <CommissionEditor
          disabled={!selectedBaseSubscription}
          isEditingSubscriptionCommission={isEditingSubscriptionCommission}
          setIsEditingSubscriptionCommission={setIsEditingSubscriptionCommission}
          baseSubscriptionId={selectedBaseSubscription?.id ?? null}
          subscriptionId={subscriptionId}
          salesRepId={commissionSchemeData.salesRepId ?? selectedClientSalesRepId}
          onUpdateScheme={onUpdateCommissionScheme}
          hasErrors={
            !(!selectedBaseSubscription) && // Not disabled
            !(
              commissionSchemeData.salesRepId
              && commissionSchemeData.scheme
              && commissionSchemeData.label.trim().length > 0
            )
          }
          fetchCommissionSchemes={fetchCommissionSchemes}
        />
      </Grid> */}
    </Grid>
  );
};
